import { format, addWeeks, addDays, isBefore, isAfter, startOfDay, addDays as addDaysFn } from 'date-fns';
import { es } from 'date-fns/locale';

export interface LeagueWeek {
  number: number;
  date: string;
  dateEnd?: string;
  fullDate: Date;
  status: 'past' | 'current' | 'future';
  time: string;
}

/**
 * Generates league weeks dynamically based on database values
 * @param startDate - The start date of the league (YYYY-MM-DD format)
 * @param totalWeeks - Total number of weeks/matches in the league
 * @param startTime - Start time in HH:MM:SS format (optional)
 * @param endTime - End time in HH:MM:SS format (optional)
 * @returns Array of LeagueWeek objects
 */
export function generateLeagueWeeks(
  startDate: string,
  totalWeeks: number,
  startTime?: string,
  endTime?: string,
  sport?: string
): LeagueWeek[] {
  const weeks: LeagueWeek[] = [];
  
  // Validate startDate
  if (!startDate || typeof startDate !== 'string') {
    console.warn('generateLeagueWeeks: Invalid startDate provided:', startDate);
    return weeks;
  }
  
  // Parse date as local to avoid UTC offset issues
  // YYYY-MM-DD parsed with new Date() is interpreted as UTC midnight,
  // which causes a 1-day shift in negative UTC offset timezones (e.g., Mexico)
  let start: Date;
  
  // Check if it's in YYYY-MM-DD format
  const dateMatch = startDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(dateMatch[3], 10);
    start = startOfDay(new Date(year, month, day));
  } else {
    // Fallback for other date formats
    start = startOfDay(new Date(startDate));
  }
  
  // Validate the parsed date
  if (isNaN(start.getTime())) {
    console.warn('generateLeagueWeeks: Could not parse startDate:', startDate);
    return weeks;
  }
  
  const today = startOfDay(new Date());
  
  // Format time range in AM/PM
  const timeRange = startTime && endTime 
    ? `${formatTo12h(startTime)} - ${formatTo12h(endTime)}`
    : '';
  
  for (let i = 0; i < totalWeeks; i++) {
    const weekDate = addWeeks(start, i);
    const weekDateNormalized = startOfDay(weekDate);
    
    // Determine week status based on 7-day range: [weekStart, weekStart+6]
    const weekEnd = startOfDay(addDays(weekDateNormalized, 6));
    let status: 'past' | 'current' | 'future';
    
    if (isAfter(today, weekEnd)) {
      status = 'past';
    } else if (!isBefore(today, weekDateNormalized) && !isAfter(today, weekEnd)) {
      status = 'current';
    } else {
      status = 'future';
    }
    
    const isTennis = sport?.toLowerCase() === 'tennis' || sport?.toLowerCase() === 'tenis';
    
    weeks.push({
      number: i + 1,
      date: format(weekDate, 'd MMM', { locale: es }),
      dateEnd: isTennis ? format(addDays(weekDate, 6), 'd MMM', { locale: es }) : undefined,
      fullDate: weekDate,
      status,
      time: timeRange
    });
  }
  
  // If no current week found, mark the first future week as current
  const hasCurrentWeek = weeks.some(w => w.status === 'current');
  if (!hasCurrentWeek) {
    const firstFutureIndex = weeks.findIndex(w => w.status === 'future');
    if (firstFutureIndex !== -1) {
      weeks[firstFutureIndex].status = 'current';
    }
  }
  
  return weeks;
}

/**
 * Calculates remaining weeks in a league
 * @param weeks - Array of LeagueWeek objects
 * @returns Number of future weeks remaining
 */
export function calculateWeeksRemaining(weeks: LeagueWeek[]): number {
  return weeks.filter(w => w.status === 'future').length;
}

/**
 * Converts a time string (HH:MM or HH:MM:SS) to 12-hour AM/PM format
 */
export function formatTo12h(time: string): string {
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr?.slice(0, 2) || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
}

/**
 * Formats time range from start and end time strings in AM/PM format
 */
export function formatTimeRange(startTime?: string, endTime?: string): string {
  if (startTime && endTime) {
    return `${formatTo12h(startTime)} - ${formatTo12h(endTime)}`;
  }
  return '';
}

/**
 * Calculates the end date of a week (start + 6 days) formatted as "d MMM" in Spanish
 * Only for tennis leagues
 */
export function calculateWeekEndDate(startDate: Date): string {
  const endDate = addDays(startDate, 6);
  return format(endDate, 'd MMM', { locale: es });
}

/**
 * Interleaves rest weeks into an encounter-only weeks array.
 * Rest weeks are inserted at their chronological positions, and encounter weeks
 * keep their original encounter number but get a new chronological `number`.
 * This keeps the calendar in sync with court booking's chronoWeekMapping.
 */
export function interleaveRestWeeks(
  encounterWeeks: LeagueWeek[],
  restWeeks: number[],
  sport?: string,
  shiftEncounterDates: boolean = false
): (LeagueWeek & { isRest?: boolean; encounterNumber?: number })[] {
  if (!restWeeks || restWeeks.length === 0) {
    return encounterWeeks;
  }

  if (encounterWeeks.length === 0) return encounterWeeks;

  const totalChronoWeeks = encounterWeeks.length + restWeeks.length;
  const result: (LeagueWeek & { isRest?: boolean; encounterNumber?: number })[] = [];
  let encounterIndex = 0;
  const isTennis = sport?.toLowerCase() === 'tennis' || sport?.toLowerCase() === 'tenis';
  const today = startOfDay(new Date());

  for (let chronoWeek = 1; chronoWeek <= totalChronoWeeks; chronoWeek++) {
    if (restWeeks.includes(chronoWeek)) {
      // Rest week — derive date from neighboring encounters (preserve real dates)
      let restDate: Date | null = null;
      const prevEncounter = [...result].reverse().find((r) => !r.isRest);
      const nextEncounter = encounterWeeks[encounterIndex];

      if (prevEncounter) {
        restDate = addDays(prevEncounter.fullDate, 7);
      } else if (nextEncounter) {
        restDate = addDays(nextEncounter.fullDate, -7);
      }

      if (!restDate) continue;

      const restNorm = startOfDay(restDate);
      const restEnd = startOfDay(addDays(restNorm, 6));
      let status: 'past' | 'current' | 'future' = 'future';
      if (isAfter(today, restEnd)) status = 'past';
      else if (!isBefore(today, restNorm) && !isAfter(today, restEnd)) status = 'current';

      result.push({
        number: chronoWeek,
        date: format(restDate, 'd MMM', { locale: es }),
        dateEnd: isTennis ? format(addDays(restDate, 6), 'd MMM', { locale: es }) : undefined,
        fullDate: restDate,
        status,
        time: '',
        isRest: true,
      });
    } else {
      // Encounter week — preserve original date from gameDates,
      // OR shift it forward by the number of rest weeks already inserted
      // when encounterWeeks come from the consecutive fallback (generateLeagueWeeks).
      if (encounterIndex < encounterWeeks.length) {
        const ew = encounterWeeks[encounterIndex];

        let displayFullDate = ew.fullDate;
        let displayDate = ew.date;
        let displayDateEnd = ew.dateEnd;
        let displayStatus = ew.status;

        if (shiftEncounterDates) {
          const restsBefore = restWeeks.filter((r) => r < chronoWeek).length;
          if (restsBefore > 0) {
            displayFullDate = addDays(ew.fullDate, restsBefore * 7);
            displayDate = format(displayFullDate, 'd MMM', { locale: es });
            if (ew.dateEnd) {
              displayDateEnd = format(addDays(displayFullDate, 6), 'd MMM', { locale: es });
            }
            // Recompute status with the shifted date
            const wkNorm = startOfDay(displayFullDate);
            const wkEnd = startOfDay(addDays(wkNorm, 6));
            if (isAfter(today, wkEnd)) displayStatus = 'past';
            else if (!isBefore(today, wkNorm) && !isAfter(today, wkEnd)) displayStatus = 'current';
            else displayStatus = 'future';
          }
        }

        result.push({
          ...ew,
          number: chronoWeek,
          encounterNumber: encounterIndex + 1,
          isRest: false,
          fullDate: displayFullDate,
          date: displayDate,
          dateEnd: displayDateEnd,
          status: displayStatus,
        });
        encounterIndex++;
      }
    }
  }

  // If no current week was marked anywhere, mark the first future encounter as current
  const hasCurrent = result.some((w) => !w.isRest && w.status === 'current');
  if (!hasCurrent) {
    const firstFuture = result.findIndex((w) => !w.isRest && w.status === 'future');
    if (firstFuture !== -1) result[firstFuture].status = 'current';
  }

  return result;
}
